import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Download, FileSpreadsheet, Loader2, Search, X } from 'lucide-react';
import { baixarPlanilhas } from '../../../../src/lib/planilhaExcel';
import {
  PERFIS_EXPORTACAO,
  itensDoRecorte,
  montarExportacao,
  perfilDaAba,
  type ContextoExportacao,
  type ItemExportavel,
  type RecorteDaTela,
} from '../lib/perfisExportacao';
import { formatCurrency } from '../utils';

/**
 * Escolhe o que vai para o Excel antes de baixar.
 *
 * O botão do cabeçalho baixava sempre a carteira inteira, com as mesmas
 * colunas em todas as abas. Aqui o modelo já vem pela aba aberta (Maiores
 * Devedores exporta o ranking, Rota de Cobrança exporta as paradas com
 * endereço) e o operador ainda escolhe quem entra: todos, os N primeiros ou a
 * dedo. "Os 30 maiores devedores de hoje" vira dois cliques.
 */

type Modo = 'tela' | 'todos' | 'top' | 'escolhidos';

/** Teto de linhas renderizadas na lista — a carteira passa de mil itens. */
const MAX_LISTA = 200;

interface Props {
  aberto: boolean;
  onFechar: () => void;
  /** Aba aberta na Central — define o modelo sugerido. */
  abaAtiva: string;
  contexto: ContextoExportacao;
  /** O que a aba está mostrando com os filtros dela — ponto de partida do modal. */
  recorteDaTela?: RecorteDaTela | null;
  onErro?: (mensagem: string) => void;
}

const ExportarExcelModal: React.FC<Props> = ({ aberto, onFechar, abaAtiva, contexto, recorteDaTela, onErro }) => {
  const [perfilId, setPerfilId] = useState(() => perfilDaAba(abaAtiva).id);
  const [modo, setModo] = useState<Modo>('todos');
  const [topN, setTopN] = useState(30);
  const [escolhidos, setEscolhidos] = useState<Set<string>>(new Set());
  const [busca, setBusca] = useState('');
  const [baixando, setBaixando] = useState(false);

  const perfil = useMemo(() => PERFIS_EXPORTACAO.find(p => p.id === perfilId) || perfilDaAba(abaAtiva), [perfilId, abaAtiva]);
  const itens = useMemo(() => (aberto ? perfil.itens(contexto) : []), [aberto, perfil, contexto]);

  // Abrir o modal recomeça do modelo da aba, já no recorte que está na tela:
  // se o operador filtrou "365 dias ou mais" ou marcou 12 paradas, é isso que
  // ele espera baixar. Sem recorte reportado, cai em "todos".
  useEffect(() => {
    if (!aberto) return;
    const doAba = perfilDaAba(abaAtiva);
    setPerfilId(doAba.id);
    setBusca('');
    setEscolhidos(new Set());
    setTopN(doAba.atalhosTopN[1] ?? doAba.atalhosTopN[0] ?? 30);
    setModo(recorteDaTela ? 'tela' : 'todos');
  }, [aberto, abaAtiva, recorteDaTela]);

  // O recorte da tela só vale para o modelo da própria aba: os ids são de
  // outra entidade (cliente x fatura x unidade) e não se traduzem entre modelos.
  const recorteAplicavel = perfilId === abaAtiva ? (recorteDaTela ?? null) : null;

  const trocarPerfil = (id: string) => {
    setPerfilId(id);
    setEscolhidos(new Set());
    setBusca('');
    if (modo === 'escolhidos' || modo === 'tela') setModo('todos');
  };

  // O recorte da tela preserva a ORDEM da tela, não a do modelo: quem filtrou e
  // ordenou lá espera a planilha na mesma sequência.
  const daTela = useMemo(() => itensDoRecorte(itens, recorteAplicavel), [recorteAplicavel, itens]);

  const selecionados = useMemo(() => {
    if (modo === 'tela') return daTela;
    if (modo === 'top') return itens.slice(0, Math.max(1, topN));
    if (modo === 'escolhidos') return itens.filter(i => escolhidos.has(i.id));
    return itens;
  }, [modo, itens, topN, escolhidos, daTela]);

  const recorte = useMemo(() => {
    if (modo === 'tela') {
      const filtros = recorteAplicavel?.descricao;
      return `Como está na tela: ${selecionados.length} de ${recorteAplicavel?.totalSemFiltro ?? selecionados.length} ${perfil.unidade}`
        + (filtros ? ` (${filtros})` : ' (sem filtro na aba)');
    }
    if (modo === 'top') return `Os ${Math.min(Math.max(1, topN), itens.length)} primeiros — ${perfil.criterioOrdem}`;
    if (modo === 'escolhidos') return `${selecionados.length} ${perfil.unidade} escolhidos um a um`;
    return `Todos os ${itens.length} ${perfil.unidade} da tela, com os filtros globais aplicados`;
  }, [modo, topN, itens.length, selecionados.length, perfil, recorteAplicavel]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const filtrados = termo
      ? itens.filter(i => i.titulo.toLowerCase().includes(termo) || i.subtitulo.toLowerCase().includes(termo))
      : itens;
    return { lista: filtrados.slice(0, MAX_LISTA), total: filtrados.length };
  }, [itens, busca]);

  const alternar = (id: string) => setEscolhidos(prev => {
    const proximo = new Set(prev);
    if (proximo.has(id)) proximo.delete(id); else proximo.add(id);
    return proximo;
  });

  const marcarVisiveis = () => setEscolhidos(prev => {
    const proximo = new Set(prev);
    visiveis.lista.forEach(i => proximo.add(i.id));
    return proximo;
  });

  const exportar = async () => {
    if (selecionados.length === 0) return;
    setBaixando(true);
    try {
      await baixarPlanilhas(montarExportacao(perfil, selecionados as ItemExportavel[], contexto, recorte), perfil.arquivo);
      onFechar();
    } catch (err) {
      console.error('Falha ao gerar o Excel da inadimplência:', err);
      onErro?.('Não consegui gerar o Excel. Tente de novo em alguns segundos.');
    } finally {
      setBaixando(false);
    }
  };

  if (!aberto) return null;

  const somaSelecionada = selecionados.reduce((s, i) => s + i.valor, 0);

  return createPortal(
    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in duration-150">
      <div className="w-full max-w-3xl max-h-[90vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">

        <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 bg-[#1d220f]">
          <div className="flex items-center gap-2.5">
            <FileSpreadsheet size={18} className="text-[#F6A937]" />
            <div>
              <h3 className="text-white font-black text-sm tracking-tight">Exportar para Excel</h3>
              <p className="text-[11px] text-gray-400">Escolha o modelo da planilha e quem entra nela.</p>
            </div>
          </div>
          <button
            onClick={onFechar}
            className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-500/20 hover:text-red-400 text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">

          {/* Modelo da planilha */}
          <div>
            <label className="block text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">
              Modelo da planilha
            </label>
            <select
              value={perfilId}
              onChange={(e) => trocarPerfil(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37] font-semibold text-gray-700"
            >
              {PERFIS_EXPORTACAO.map(p => (
                <option key={p.id} value={p.id}>
                  {p.titulo}{p.id === abaAtiva ? ' — tela atual' : ''}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-gray-500 leading-snug">{perfil.descricao}</p>
          </div>

          {/* Quem entra */}
          <div>
            <label className="block text-[10px] font-black uppercase text-gray-400 tracking-wider mb-2">
              Quem entra na planilha
            </label>
            <div className="flex flex-wrap items-center gap-2">
              {recorteAplicavel && (
                <BotaoModo ativo={modo === 'tela'} onClick={() => setModo('tela')}>
                  Como está na tela ({daTela.length})
                </BotaoModo>
              )}
              <BotaoModo ativo={modo === 'todos'} onClick={() => setModo('todos')}>
                Todos ({itens.length})
              </BotaoModo>
              <BotaoModo ativo={modo === 'top'} onClick={() => setModo('top')}>
                Os primeiros
              </BotaoModo>
              <BotaoModo ativo={modo === 'escolhidos'} onClick={() => setModo('escolhidos')}>
                Escolher a dedo{escolhidos.size > 0 ? ` (${escolhidos.size})` : ''}
              </BotaoModo>
            </div>

            {modo === 'tela' && (
              <p className="mt-2 text-xs text-gray-500">
                {recorteAplicavel?.descricao
                  ? <>Respeitando os filtros da aba: <strong>{recorteAplicavel.descricao}</strong>.</>
                  : <>A aba está sem filtro próprio — vão os {daTela.length} {perfil.unidade} que ela mostra.</>}
              </p>
            )}

            {modo === 'top' && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {perfil.atalhosTopN.map(n => (
                  <button
                    key={n}
                    onClick={() => setTopN(n)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
                      topN === n ? 'bg-[#5F6C37] text-white border-[#5F6C37]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#5F6C37]'
                    }`}
                  >
                    Top {n}
                  </button>
                ))}
                <input
                  type="number"
                  min={1}
                  max={itens.length || 1}
                  value={topN}
                  onChange={(e) => setTopN(Number(e.target.value) || 1)}
                  className="w-24 px-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37]"
                />
                <span className="text-xs text-gray-500">{perfil.criterioOrdem}</span>
              </div>
            )}

            {modo === 'escolhidos' && (
              <div className="mt-3 border border-gray-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      placeholder="Buscar na lista..."
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-[#5F6C37] focus:border-[#5F6C37]"
                    />
                    <Search size={13} className="absolute left-2.5 top-2 text-gray-400" />
                  </div>
                  <button onClick={marcarVisiveis} className="px-2.5 py-1.5 text-[11px] font-bold text-[#5F6C37] hover:bg-[#5F6C37]/10 rounded-lg whitespace-nowrap">
                    Marcar os da lista
                  </button>
                  <button onClick={() => setEscolhidos(new Set())} className="px-2.5 py-1.5 text-[11px] font-bold text-gray-500 hover:bg-gray-100 rounded-lg">
                    Limpar
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto divide-y divide-gray-100">
                  {visiveis.lista.length === 0 && (
                    <p className="px-3 py-6 text-center text-xs text-gray-500">Nada encontrado com esse termo.</p>
                  )}
                  {visiveis.lista.map(item => {
                    const marcado = escolhidos.has(item.id);
                    return (
                      <button
                        key={item.id}
                        onClick={() => alternar(item.id)}
                        className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${marcado ? 'bg-[#5F6C37]/[0.06]' : 'hover:bg-gray-50'}`}
                      >
                        <span className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                          marcado ? 'bg-[#5F6C37] border-[#5F6C37]' : 'border-gray-300'
                        }`}>
                          {marcado && <Check size={12} className="text-white" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-xs font-bold text-gray-800 truncate">{item.titulo}</span>
                          <span className="block text-[11px] text-gray-500 truncate">{item.subtitulo}</span>
                        </span>
                        {item.valor > 0 && (
                          <span className="text-xs font-black text-gray-700 flex-shrink-0">{formatCurrency(item.valor)}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
                {visiveis.total > visiveis.lista.length && (
                  <p className="px-3 py-2 text-[11px] text-gray-500 bg-gray-50 border-t border-gray-200">
                    Mostrando {visiveis.lista.length} de {visiveis.total}. Use a busca para chegar no resto — o que já está marcado continua marcado.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
            <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider mb-1">Vai sair na planilha</p>
            <p className="text-sm font-bold text-gray-800">
              {selecionados.length} {perfil.unidade}
              {somaSelecionada > 0 && <span className="text-[#5F6C37]"> · {formatCurrency(somaSelecionada)}</span>}
            </p>
            <p className="text-[11px] text-gray-500 mt-0.5">{recorte}</p>
          </div>
        </div>

        <div className="flex-shrink-0 flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 border-t border-gray-200">
          <button onClick={onFechar} className="px-4 py-2 text-xs font-bold text-gray-600 hover:bg-gray-200 rounded-lg transition-colors">
            Cancelar
          </button>
          <button
            onClick={exportar}
            disabled={baixando || selecionados.length === 0}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {baixando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
            {baixando ? 'Gerando...' : 'Baixar Excel'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const BotaoModo: React.FC<{ ativo: boolean; onClick: () => void; children: React.ReactNode }> = ({ ativo, onClick, children }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-colors ${
      ativo ? 'bg-[#5F6C37] text-white border-[#5F6C37]' : 'bg-white text-gray-600 border-gray-300 hover:border-[#5F6C37]'
    }`}
  >
    {children}
  </button>
);

export default ExportarExcelModal;
